/**
 * Editorial journal entries. `product` names a catalog item whose image and price
 * are pulled live from the API when the article is rendered.
 */
export const JOURNAL_POSTS = [
  {
    slug: "the-ambdrop-way",
    eyebrow: "The house",
    title: "The Ambdrop way: luxury, poured slow.",
    date: "Vol. 01 · Letter I",
    excerpt:
      "Why a small Indian atelier decided that fine fragrance should not cost a month's salary — and what it took to make that true.",
    product: null,
    body: [
      "Ambdrop began with a simple frustration. The perfumes we loved sat behind glass counters at prices built for duty-free shelves, not for the dresser of someone who simply wanted to smell wonderful every day. Fine fragrance had become a luxury of price rather than a luxury of craft.",
      "So we started small. A handful of accords, composed around moments instead of marketing — a monsoon afternoon, a winter ballroom, a Tuesday at the desk. We sourced raw material from growers we know by name: Mysore sandalwood, Kannauj attar, Calabrian bergamot. And we chose to decant by hand into 30ml flacons, because a perfume you finish is worth more than one that sits half-full for a decade.",
      "Everything that does not touch the scent itself — the celebrity campaign, the velvet box, the counter in the mall — we removed. What remains is the juice, the bottle, and a price that lets you wear it generously. That is the Ambdrop way: luxury at an affordable price. Your scent, your story.",
    ],
  },
  {
    slug: "rouge-amb-56",
    eyebrow: "Best seller",
    title: "Rouge Amb 56 — a winter ballroom in a bottle.",
    date: "Vol. 01 · Letter II",
    excerpt:
      "Our most-poured women's fragrance opens like red silk and settles into something warm, resinous and unforgettable.",
    product: "Rouge Amb 56",
    body: [
      "Rouge Amb 56 is the fragrance people write to us about. It opens bright and a little daring — ripe red fruit and a flash of pink pepper — before the heart unfolds into a velvet bouquet of rose and jasmine. By the second hour it has settled into its true character: warm amber, soft vanilla and a thread of smoky wood that lingers on a coat collar until morning.",
      "We composed it for parties and winter evenings, for rooms with chandeliers and conversations that run late. It wears close in the cold and blooms in a warm room, which is exactly how a party fragrance should behave.",
      "A little goes a long way. Two sprays at the collarbone are enough to carry an evening — and with Buy 1 Get 1 on every flacon this season, there is one for the dresser and one for the bag.",
    ],
  },
  {
    slug: "sauvage-amb-60",
    eyebrow: "Best seller",
    title: "Sauvage Amb 60 — the everyday signature for him.",
    date: "Vol. 01 · Letter III",
    excerpt:
      "Fresh bergamot over ambroxan and pepper: the men's fragrance that works at nine in the morning and nine at night.",
    product: "Sauvage Amb 60",
    body: [
      "Some fragrances are for occasions. Sauvage Amb 60 is for every day. It begins with a cold, clean rush of Calabrian bergamot, sharpened with Sichuan pepper, and dries down into a mineral, ambery base that feels like warm skin after a walk in the open air.",
      "It is the bottle our customers buy twice — once for themselves and once for someone they want to smell like it. It is confident without shouting, fresh without being fleeting, and it holds its line from the office to a late dinner without needing a top-up.",
      "If you are choosing one Ambdrop fragrance for a man who does not yet have a signature, this is the one we point to first.",
    ],
  },
  {
    slug: "flora-amb-55",
    eyebrow: "Best seller",
    title: "Flora Amb 55 — summer, in full bloom.",
    date: "Vol. 01 · Letter IV",
    excerpt:
      "A luminous floral built for heat: gardenia, pear and jasmine lifted by a clean musk that stays bright all day.",
    product: "Flora Amb 55",
    body: [
      "Summer asks a lot of a perfume. It must be fresh without disappearing, floral without turning heavy in the heat. Flora Amb 55 answers with a sun-lit opening of crisp pear and mandarin, a heart of gardenia and jasmine sambac, and a soft, clean musk that keeps the whole composition airy from morning to dusk.",
      "We wear it to brunches, to beach towns, to long afternoons that turn into evenings. It is generous and joyful, and it photographs beautifully — which is to say, people ask what you are wearing.",
      "Flora sits in our For Women, For Summer and All Weather categories, and it is one of the three flacons we would pack if we could only pack three.",
    ],
  },
  {
    slug: "blue-de-amb-65",
    eyebrow: "Best seller",
    title: "Blue de Amb 65 — the unisex that belongs in every office.",
    date: "Vol. 01 · Letter V",
    excerpt:
      "Citrus, incense and cedar in a quiet, polished composition that reads as competence from across a boardroom.",
    product: "Blue de Amb 65",
    body: [
      "There is a particular kind of fragrance that makes people lean in without quite knowing why. Blue de Amb 65 is that fragrance. A bright grapefruit-and-lemon opening gives way to a cool heart of incense and nutmeg, finished with dry cedar and a whisper of labdanum.",
      "It is genuinely unisex — we sell it in equal measure to men and women — and it is composed to sit politely in a meeting room and then surprise someone at the lift. It never shouts. It simply lasts.",
      "Pair it with a crisp shirt and an early start. Blue de Amb 65 lives in our Unisex, For Office and All Weather categories.",
    ],
  },
  {
    slug: "affordable-luxury",
    eyebrow: "Our vision",
    title: "How we keep luxury affordable — honestly.",
    date: "Vol. 01 · Letter VI",
    excerpt:
      "No counters, no campaigns, no velvet boxes. A look at exactly where your rupees go when you buy an Ambdrop flacon.",
    product: null,
    body: [
      "A luxury perfume at a department-store counter spends most of its price on things you cannot smell: the retail lease, the distributor, the advertising, the packaging designed to be thrown away. We decided that the only thing worth paying for is what ends up on your skin.",
      "Ambdrop sells directly to you. We photograph our own bottles, write our own letters, and ship from the atelier with free delivery across India. The savings are not a discount; they are the whole model. Our perfumes are composed with the same raw materials used by houses charging many times more.",
      "This is our vision in a sentence: luxury at an affordable price, so that your fragrance can be a daily pleasure rather than an annual indulgence. Your scent, your story — told every single morning.",
    ],
  },
];

export const findPost = (slug) => JOURNAL_POSTS.find((p) => p.slug === slug);
